import { Component, ElementRef, inject, NgZone, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { AppWebBridgeService, OperatingSystem } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { EventLoggerService } from '@services/event-logger-service';
import { CookieService } from '@services/cookie-service';
import { BroadcastService } from '@services/broadcast-service';
import { SentryErrorHandler } from '@services/sentry-logger';
import { LoginService } from '@services/login-service/login.service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { ShiprocketLoginService } from '@services/shiprocket-login';
import { CurrentComponentService } from '@services/current-component';
import { AuthConfig } from '../../app-constant-types';
import {AppConfig} from '../../app.config';

declare const grecaptcha: any;
const reCAPTCHAKey = '6Le9rtAmAAAAAAJNpaytF64IHj5rrRtK7iXbcEOe';
const SHIPROCKET_COUNTRY_CODE = '+91';
const SHIPROCKET_LOGIN_METHOD = 'shiprocket';

@Component({
  selector: 'onboarding-number',
  templateUrl: './onboarding-mobile-number.html',
  standalone: false,
})
export class OnboardingMobileNumberComponent {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly router: Router = inject(Router);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly cookies: CookieService = inject(CookieService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly zone: NgZone = inject(NgZone);
  private readonly sentryErrorHandler: SentryErrorHandler = inject(SentryErrorHandler);
  private readonly loginService: LoginService = inject(LoginService);
  public readonly commonUtilityHelper: CommonUtilityHelper = inject(CommonUtilityHelper);
  protected readonly appConfig: AppConfig = inject(AppConfig);
  private readonly shiprocketService: ShiprocketLoginService = inject(ShiprocketLoginService);
  private readonly currentComponent: CurrentComponentService = inject(CurrentComponentService);
  private readonly windowRefService: WindowRefService = inject(WindowRefService);

  reCAPTCHAKey: string = reCAPTCHAKey;
  subscriptions: Array<Subscription> = [];
  loading: boolean = false;
  phoneNumber: string;
  startTimer: number;
  mobileNumberError: boolean = false;
  loginWithShiprocket: boolean = false;
  isiOSDevice: boolean = false;
  shiprocketConsent: boolean = true;
  @ViewChild('scrollContainer') scrollContainer: ElementRef;

  get isPhoneNumberValid(): boolean {
    return this.commonUtilityHelper.isValidNumberInput(this.phoneNumber, 10);
  }

  ngOnInit(): void {
    this.loginWithShiprocket = this.appWebBridge.isShiprocketLoginEnabled();
    this.appWebBridge.requestOSInformation().then((os: string): void => {
      this.isiOSDevice = os === OperatingSystem.IOS || this.windowRefService.isSafariBrowser();
    });
    this.appWebBridge.setStatusBarColor('cure/light');
    this.currentComponent.set(this);
    this.startTimer = new Date().getTime();
    this.phoneNumber = this.localStorageService.getValue('CureSkin/mobileNumber') || '';
    this.appWebBridge.notifyRequestSmsPermission();
    this.subscriptions.push(this.broadcast.on<string>('AUTO_FILL_NUMBER')
      .subscribe((phone: string): void => this.zone.run((): void => {
        if (this.phoneNumber === phone) return;
        this.phoneNumber = phone;
        if (this.phoneNumber.length === 12 && this.phoneNumber.startsWith('91')) {
          this.phoneNumber = this.phoneNumber.substring(2);
        }
      })));

    /**
     * This broadcast sends you object with all necessary data to login (i.e source, mobile number, otp, ids, etc).
     * This is used for 'Login with TrueCaller' feature. Truecaller authenticates the user and sends the data from android.
     * So in this case, we ignore number/otp method and login directly with truecaller auth.
     */
    this.subscriptions.push(this.broadcast.on<string>('GET_AUTHENTICATION_INFO')
      .subscribe((authInfo: string): void => this.zone.run((): void => {
        this.handleGetAuthenticationInfo(JSON.parse(JSON.stringify(authInfo)));
      })));
    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('onboarding_number_page_loading_time', { timeInMilliSec: time });
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'onboarding-mobile-number' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'pageOpenMobilenumber' },
      firebase: { name: 'pageOpenMobilenumber' },
    });
  }

  private async handleGetAuthenticationInfo(authInfo: AuthConfig): Promise<void> {
    try {
      this.loading = true;
      await this.loginService.authenticateUser(JSON.parse(JSON.stringify(authInfo)));
    } catch (error) {
      if (error instanceof Error) {
        this.sentryErrorHandler.handleError(error.toString());
      }
      this.eventLogger.trackEvent('auth_failure', { error: error.toString() });
    } finally {
      this.loading = false;
    }
  }

  onPhoneNumberChange(): void {
    this.phoneNumber = this.phoneNumber?.replace(/[^0-9]/g, '');
    this.mobileNumberError = false;
  }

  async requestOTP(): Promise<void> {
    if (!this.isPhoneNumberValid) {
      this.mobileNumberError = true;
      return;
    }

    if (this.loginWithShiprocket && this.shiprocketConsent) {
      await this.requestShiprocketOTP();
      return;
    }

    this.shiprocketService.token = null;
    grecaptcha.enterprise.ready(async (): Promise<void> => {
      this.loading = true;
      this.localStorageService.setValue('CureSkin/mobileNumber', this.phoneNumber);
      const deviceId = this.cookies?.get('deviceId');
      const source = await this.appWebBridge.requestOSInformation();
      this.eventLogger.trackEvent('otp_requested', { device_id: deviceId, mobileNumber: this.phoneNumber });
      this.eventLogger.cleverTapEvent('otpRequested', JSON.stringify({ loginMethod: 'otp' }));
      this.eventLogger.timeEvent('otp_requested_time', true);
      this.appWebBridge.requestStartSMSListener();
      this.eventLogger.trackInElasticSearch({
        added: new Date(),
        type: 'webApp',
        message: `device_id: ${deviceId} , mobileNumber: ${this.phoneNumber}`,
        event: 'REQUEST_OTP',
      });
      try {
        const recaptchaToken: string = await grecaptcha.enterprise.execute(this.reCAPTCHAKey, { action: 'requestOTP' });
        await this.conn.requestOTP(
          `${this.phoneNumber}`,
          source,
          { recaptcha: { action: 'requestOTP', token: recaptchaToken } });
        this.eventLogger.timeEvent('otp_requested_time');
        this.eventLogger.trackEvent('saved_mobile_number', {});
        const time = new Date().getTime() - this.startTimer;
        this.eventLogger.trackEvent('time_spent_on_filling_number', { timeInMilliSec: time });
        this.router.navigate(['/onboarding/otp']);
      } catch (error) {
        this.loading = false;
        const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
        this.broadcast.broadcast('NOTIFY', { message: errorMessage });
        this.sentryErrorHandler.handleError(errorMessage);
      }
    });
  }

  private async requestShiprocketOTP(): Promise<void> {
    this.loading = true;
    this.localStorageService.setValue('CureSkin/mobileNumber', this.phoneNumber);
    this.shiprocketService.userAddressConsent = this.shiprocketConsent;
    const deviceId = this.cookies?.get('deviceId') || this.conn.uniqueId;
    const source = await this.appWebBridge.requestOSInformation();
    this.eventLogger.trackEvent('otp_requested', { device_id: deviceId, mobileNumber: this.phoneNumber, loginMethod: SHIPROCKET_LOGIN_METHOD });
    this.eventLogger.cleverTapEvent('otpRequested', JSON.stringify({ loginMethod: SHIPROCKET_LOGIN_METHOD }));
    this.appWebBridge.requestStartSMSListener();
    try {
      const { code } = await this.shiprocketService.generate({
        countryCode: SHIPROCKET_COUNTRY_CODE,
        mobileNumber: `${this.phoneNumber}`,
        source,
        deviceId,
      });
      this.shiprocketService.token = code;
      this.eventLogger.trackEvent('saved_mobile_number', {});
      this.router.navigate(['/onboarding/otp']);
    } catch (error) {
      this.loading = false;
      const errorMessage = error instanceof Error ? error.message : $localize`:@@somethingWentWrongTryAgain:Something went wrong. Please try again.`;
      this.broadcast.broadcast('NOTIFY', { message: errorMessage });
      this.sentryErrorHandler.handleError(errorMessage);
    }
  }

  navigateTo(url: string): void {
    this.conn.navigateToURL(url);
  }

  /** Opens an external URL outside the in-app iframe (Chrome Custom Tab on Android,
   * new browser tab on iOS/web). Used for Shiprocket's T&C / Privacy pages, which
   * block iframe embedding via X-Frame-Options / CSP frame-ancestors. */
  openExternalUrl(url: string): void {
    if (this.isiOSDevice) this.conn.navigateToURL(url);
    else this.broadcast.broadcast('OPEN_IN_NEW_TAB', { url });
  }

  back(): void {
    this.router.navigate(['/language'], { queryParams: { force: true } });
  }

  /** Keeps the input and CTA visible when the on-screen keyboard shrinks the viewport. */
  onInputFocus(): void {
    setTimeout((): void => {
      if (!this.scrollContainer) return;
      const container = this.scrollContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }, 300);
  }

  /** Android back: take the user to the language page so they can change language. */
  async handleBackPress(): Promise<boolean> {
    this.back();
    return true;
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.currentComponent.remove(this);
  }

  public unsubscribe(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.subscriptions = [];
  }
}
